import {
  collection,
  getCountFromServer,
  query,
  where
} from 'firebase/firestore';
import { db, } from '@util/firebase';

import { getCdnImagePath, getStoragePathFromCdnUrl } from '@util/urlHelpers';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';

const storage = getStorage();

export async function uploadListingImage({
  data,
  userId,
  productId,
}: {
  data: Blob;
  userId: string;
  productId: string;
}) {
  const path = `${userId}/products/${productId}/${Date.now()}`;
  const listingImagesRef = ref(storage, path);
  const res = await uploadBytes(listingImagesRef, data);
  return res.metadata.fullPath;
}

export async function deleteListingImage(fullPath: string) {
  const cdnPaths = getCdnImagePath(fullPath);
  const { full, thumb } = getStoragePathFromCdnUrl(cdnPaths);

  [full, thumb].forEach(async (path) => {
    const listingImagesRef = ref(storage, path);
    deleteObject(listingImagesRef);
  });
}

export async function uploadUserImage(data: Blob, userId: string) {
  const path = `${userId}/avatar-${Date.now()}`;
  const userImagesRef = ref(storage, path);
  const res = await uploadBytes(userImagesRef, data);
  return res.metadata.fullPath;
}

export async function uploadStoreImage(data: Blob, userId: string) {
  const path = `${userId}/store-${Date.now()}`;
  const storeImageRef = ref(storage, path);
  const res = await uploadBytes(storeImageRef, data);
  return res.metadata.fullPath;
}

// GB #1612: own path prefix (store-mobile- vs store-) so a desktop banner
// upload and a mobile banner upload in the same millisecond cannot collide.
export async function uploadStoreMobileImage(data: Blob, userId: string) {
  const path = `${userId}/store-mobile-${Date.now()}`;
  const storeMobileImageRef = ref(storage, path);
  const res = await uploadBytes(storeMobileImageRef, data);
  return res.metadata.fullPath;
}

// GB #1612 -- Codex "most serious finding of twenty": removeUserKey only
// ever called Firestore's deleteField() -- nothing deleted the actual
// Storage object, so a removed (or superseded/stale-discarded) banner
// stayed publicly reachable at its CDN URL forever. A seller who uploaded
// a banner with a phone number or a face on it and clicked Remove had NOT
// removed it.
//
// Mirrors deleteListingImage's established full+thumb deletion pattern
// exactly (same getCdnImagePath/getStoragePathFromCdnUrl helpers), given
// its own name here since it is called from a banner-specific context, not
// a listing one. Unlike deleteListingImage's fire-and-forget style, this
// awaits both deletions and only swallows storage/object-not-found (e.g.
// no thumbnail was ever generated for this particular upload) -- any other
// failure (permissions, network) propagates so the caller knows removal
// did not actually happen and can surface that honestly instead of
// claiming success.
export async function deleteStoreBannerImage(fullPath: string): Promise<void> {
  const cdnPaths = getCdnImagePath(fullPath);
  const { full, thumb } = getStoragePathFromCdnUrl(cdnPaths);

  // GB #1612 -- Codex sixth review, finding 1: `getCdnImagePath` only ever
  // returns DERIVED paths (fullPath + '.jpeg', and a thumb variant) -- it
  // never returns fullPath itself. uploadBytes() writes the ORIGINAL
  // object at the extensionless fullPath (e.g. "uid/store-mobile-123"),
  // and that is the object every caller actually means to delete. Only
  // deleting the two derived paths left the real uploaded object
  // permanently in Storage on every Remove and every stale-upload
  // cleanup -- a seller who clicked Remove on a banner with a phone
  // number, a face, or a pricing mistake had NOT removed it; the raw
  // object was still sitting in the bucket the whole feature exists to
  // let them clear out.
  await Promise.all(
    [fullPath, full, thumb].map(async (path) => {
      try {
        await deleteObject(ref(storage, path));
      } catch (err) {
        const code = (err as { code?: string } | undefined)?.code;
        if (code !== 'storage/object-not-found') {
          throw err;
        }
      }
    })
  );
}

export async function getSoldListingsCount(seller_id: string) {
  const q = query(
    collection(
      db,
      'products'
    ),
    where('sold_count', '>=', 1),
    where('seller_id', '==', seller_id)
  );
  const res = await getCountFromServer(q);
  return res.data()?.count ?? 0;
}

export async function getActiveListingsCount(seller_id: string) {
  const q = query(
    collection(
      db,
      'products'
    ),
    where('is_verified', '==', true),
    where('is_draft', '==', false),
    where('is_expired', '==', false),
    where('stock', '>=', 1),
    where('out_of_stock', '==', false),
    where('seller_id', '==', seller_id)
  );
  const res = await getCountFromServer(q);
  return res.data()?.count ?? 0;
}